/**
 * SOLID Review
 * S — Strong. One concern: the consent management platform as the `ClientConsentPlatform` port speaks
 *     it — booting it once, and delivering every state it reports to the Application.
 * O — Good. A new CMP call is one more method over the same gateway.
 * L — Strong. Implements the port interface exactly.
 * I — Strong. The port's three ops.
 * D — Good. The Application-declared gateway interface, the delivery port and the clock, all injected.
 */
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { ICommandCookiePreferences, IEntityPreferencesOpened, IPayloadConsentState } from '../../domain/ports/schemas/ConsentMoves';
import type { IPortClientConsentPlatform } from '../../application/ports/contracts';
import type { IPortClientServerEvents, IPayloadClientServerEvent } from '../../application/ports/ClientServerEvents.contract';
import type { IGatewayConsent } from '../../application/consent/IGatewayConsent';
import type { IServiceClock } from '../platform/IServiceClock';

/** How long a delivered consent change may take before its decision is refused (the guard's DEADLINE). */
const DELIVERY_BUDGET_MS = 10_000;

export interface IConfigDepConsentPlatform {
  /** The CMP — a host resource: Funding Choices lives in the browser's main thread. */
  readonly gateway: IGatewayConsent;
  /** Where the CMP's changes go: the Application's delivery lollipop. */
  readonly events: IPortClientServerEvents;
  /** A delivery's deadline is read from it — the host's clock, never `Date`'s (frontend-infrastructure-oo P4, §28.289). */
  readonly clock: IServiceClock;
}

/**
 * THE CONSENT PLATFORM (frontend strict-ports plan, FE4b, 2026-10-04) — CookieConsentProvider's
 * subscription and boot, moved out of React. `start` subscribes and boots the CMP once (the provider's
 * module-level `initialized`, an instance field now — one adapter per graph), and every state the CMP
 * reports — including the replay a new subscriber gets — is DELIVERED as `consentChanged`, as the server's
 * events are: the Application decides what it means.
 */
export class ClientConsentPlatformAdapter implements IPortClientConsentPlatform {
  private started = false;
  private deliveries = 0;

  constructor(private readonly deps: IConfigDepConsentPlatform) {}

  async start(_ctx: IContextRequest) {
    if (!this.started) {
      this.started = true;
      this.deps.gateway.subscribe((state) => this.deliver(state));
      this.deps.gateway.init();
    }
    return { success: true as const, value: undefined };
  }

  async openPreferences(_ctx: IContextRequest) {
    const opened: IEntityPreferencesOpened = { opened: this.deps.gateway.openPreferences() };
    return { success: true as const, value: opened };
  }

  async applyManualConsent(_ctx: IContextRequest, command: ICommandCookiePreferences) {
    this.deps.gateway.applyManualConsent(command.categories);
    return { success: true as const, value: undefined };
  }

  /** Delivered as it arrives, not awaited — as the server's events are. */
  private deliver(state: IPayloadConsentState): void {
    this.deliveries += 1;
    const ctx: IContextRequest = { requestId: `consent-${this.deliveries}`, deadlineAt: this.deps.clock.now() + DELIVERY_BUDGET_MS };
    const payload: IPayloadConsentState = {
      resolved: state.resolved,
      categories: { ...state.categories },
      region: state.region,
      source: state.source,
      tcString: state.tcString,
    };
    const event: IPayloadClientServerEvent = { type: 'consentChanged', payload };
    void this.deps.events.deliver(ctx, event);
  }
}
